<script setup>
import { computed } from "vue";
import { Link } from "@inertiajs/vue3";

const props = defineProps({
  data: { type: Object, default: () => ({ items: [], total: 0 }) },
  categoryName: { type: String, default: "" },
  city: { type: String, default: "" },
  questionsUrl: { type: String, default: "/questions" },
});

const items = computed(() => props.data?.items || []);
const total = computed(() => props.data?.total || 0);

const heading = computed(() => {
  const base = props.categoryName
    ? `Вопросы юристам по теме «${props.categoryName}»`
    : "Вопросы юристам";
  return props.city ? `${base} — ${props.city}` : base;
});

const answersWord = (n) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return "ответов";
  if (b > 1 && b < 5) return "ответа";
  if (b === 1) return "ответ";
  return "ответов";
};
</script>

<template>
  <section v-if="items.length" id="user-questions" class="max-w-7xl mx-auto px-4 py-8 md:py-12 scroll-mt-24">
    <div class="flex items-center gap-3 mb-5">
      <span class="flex-grow h-px bg-gray-200"></span>
      <h2 class="text-2xl lg:text-3xl font-extrabold tracking-tight text-gray-900 text-center">
        {{ heading }}
      </h2>
      <span class="flex-grow h-px bg-gray-200"></span>
    </div>

    <p class="text-center text-gray-500 text-sm mb-6 max-w-2xl mx-auto">
      Реальные вопросы людей по этой теме и ответы юристов. Не нашли свой случай — задайте вопрос, ответят бесплатно.
    </p>

    <div class="grid gap-3 md:grid-cols-2">
      <Link
        v-for="q in items"
        :key="q.url"
        :href="'/questions/' + q.url"
        class="block rounded-2xl border border-gray-100 bg-white px-5 py-4 hover:border-indigo-200 hover:shadow-sm transition"
      >
        <h3 class="font-semibold text-gray-900 leading-snug mb-1.5 line-clamp-2">{{ q.header }}</h3>
        <p class="text-sm text-gray-500 leading-relaxed line-clamp-2">{{ q.snippet }}</p>
        <div class="flex items-center gap-3 mt-3 text-xs text-gray-500">
          <span v-if="q.answers" class="inline-flex items-center gap-1 text-indigo-600 font-medium">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 17h6l3 3v-3h2V9h-2M4 4h11v8H9l-3 3v-3H4V4Z" />
            </svg>
            {{ q.answers }} {{ answersWord(q.answers) }} юристов
          </span>
          <span v-else>Ждёт ответа юриста</span>
          <span v-if="q.city" class="inline-flex items-center gap-1">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {{ q.city }}
          </span>
        </div>
      </Link>
    </div>

    <div class="flex flex-wrap items-center justify-center gap-3 mt-6">
      <a :href="questionsUrl"
         class="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
        Все вопросы по теме<span v-if="total"> ({{ total }})</span>
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
        </svg>
      </a>
      <a href="/questions/add"
         class="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 transition">
        Задать свой вопрос
      </a>
    </div>
  </section>
</template>
